import React, { createContext, useState } from 'react';
import ChatAPI, { Message, MessageType } from '../chat-api';
import axios from 'axios';
import { useChatState } from '.';

export interface MessageContextTypes {
  scrollBottom: boolean;
  setScrollBottom(state: boolean): void;
  messages: Message[];
  setMessages(state: any): void;
  sendingMessages: Message[];
  setSendingMessages(state: any): void;
  isRecordingAudio: boolean;
  setIsRecordingAudio(state: any): void;
  sendMessage(args: any): Promise<any>;
  uploadAndSend(file: File, messageType: MessageType): void;
}

export const MessageContext = createContext<MessageContextTypes>(null!);

const MessageContextProvider: React.FC = React.memo(({children}) => {
  const chatAPI = new ChatAPI();
  const { user } = useChatState();
  const [scrollBottom, setScrollBottom] = useState<boolean>(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [sendingMessages, setSendingMessages] = useState<Message[]>([]);
  const [isRecordingAudio, setIsRecordingAudio] = useState<boolean>(false);
  const contextState = {
    scrollBottom,
    setScrollBottom,
    messages, 
    setMessages,
    sendingMessages, 
    setSendingMessages,
    isRecordingAudio, 
    setIsRecordingAudio
  }; 

  const uploadAndSend: MessageContextTypes['uploadAndSend'] = (file: File, messageType: MessageType) => {
    const message = chatAPI.genMessage({
      type: messageType,
      cancelToken: axios.CancelToken.source(),
      body: {
        file: {
          _id: Math.random().toString(),
          name: file.name,
          length: file.size,
          contentType: file.type
        }
      }
    });
    setSendingMessages(prev => [...prev, message]);
    setScrollBottom(true);
    chatAPI.uploadFile(file, message.cancelToken).then(res => {
      return chatAPI.sendMessage({
        from: user,
        type: messageType,
        body: {
          file: {
            _id: res._id,
            collection: res.collection,
            contentType: messageType === MessageType.Audio ? 'audio/mpeg' : res.contentType,
            hash: res.hash,
            length: res.length,
            name: res.name,
            storage: res.storage,
          },
        },
        sendHash: message.sendHash
      });
    }).finally(() => {
      setSendingMessages(prev => prev.filter(obj => obj._id !== message._id));
    });
  }

  const sendMessage: MessageContextTypes['sendMessage'] = (args: any) => {
    const message = chatAPI.genMessage(args);
    setSendingMessages(prev => [...prev, message]);
    setScrollBottom(true);
    return chatAPI.sendMessage({...args, from: user, sendHash: message.sendHash})
      .finally(() => {
        setSendingMessages(prev => prev.filter(obj => obj._id !== message._id))
      });
  }

  return (
    <MessageContext.Provider value={{...contextState, sendMessage, uploadAndSend} as MessageContextTypes}>
      {children}
    </MessageContext.Provider>
  );
});


export default MessageContextProvider;